import {unitMediaSummary} from './unit-media-summary';
import {CourseMediaAssetsContext} from './CourseMediaAssetsContext';
import {EmptyLesson} from '../media/MediaPlayer/MediaPlayer.style';
import {mediaSourceUrl, MediaSourceInput} from '../media/MediaPlayer';
import * as React from 'react';

import {FileProps, InputEvent} from '../../types';

import {
  UnitContainer,
  UnitContainerHead,
  UnitContainerMinified,
  UnitSummaryThumbnail,
  UnitSummaryDetails,
  UnitSummaryBadges,
  UnitDetailsButton,
  UnitSelectField,
  UnitSelectFields,
} from './UnitComponents.style';

import { Input as TextInput, Textarea } from '../../../components/ui';

import Select from '../../elements/Select';

import {
  ButtonDeleteComponent,
  ButtonState,
  ButtonToggleContentClean,
} from '../button';

import {ButtonRowHeaderContainer} from '../button/Button.style';
import {UnitMediaComponent} from './UnitMediaComponent';

import {CourseModuleContext} from '../../context/CourseModuleContext';
import {useUnit} from './unit';
import {TranslateMediaValueToFileProps} from '../../../utils/Media';

type UnitEmptyComponentProps = {
  createUnit: InputEvent;
};

interface UnitProps {
  id: number | string;
  title: string;
  description: string;
  unitType: 'Free' | 'Premium';
  coverUrl?: string;
  posterUrl?: string;
  videoUrl?: string;
  audioUrl?: string;
  participationType: 'None' | 'Image' | 'Audio' | 'Video' | 'All';
  externalVideoUrl?: string;
  cover?: MediaSourceInput;
  poster?: MediaSourceInput;
  video?: MediaSourceInput;
  audio?: MediaSourceInput;
  alternativeVideo?: MediaSourceInput;
  alternative_video?: MediaSourceInput;
}

interface UnitErrorsProps {
  title?: string;
  description?: string;
}

interface UnitComponentProps {
  unit: UnitProps;
  index: number;
  errors?: UnitErrorsProps;
}

const unitTypeOptions = [
  {value: 'Free', label: 'Gratuita'},
  {value: 'Premium', label: 'Premium'},
];

const participationTypeOptions = [
  {value: 'none', label: 'Nenhum'},
  {value: 'image', label: 'Imagem'},
  {value: 'audio', label: 'Áudio'},
  {value: 'video', label: 'Vídeo'},
  {value: 'all', label: 'Todos'},
];
export const UnitComponent = ({unit, index, errors}: UnitComponentProps) => {
  const {
    setUnit,
    deleteUnit,
    units,
    index: courseModuleIndex,
  } = React.useContext(CourseModuleContext);

  const _useUseCase = useUnit({
    unit,
    index,
    setUnit: setUnit as any,
    deleteUnit: deleteUnit as any,
    errors: errors as any,
    unitsLength: units?.length,
  });

  const assets = React.useContext(CourseMediaAssetsContext);
  const summary = unitMediaSummary(unit, assets);

  return (
    <li>
      <UnitContainer>
        <UnitContainerHead className="row" errors={errors}>
          <TextInput
            s={6}
            l={10}
            id={`coursemodule-${courseModuleIndex}-unit-${index}`}
            name={`title`}
            onChange={_useUseCase.handleChangeField('title')}
            value={unit.title || ''}
            placeholder={`Escreva o título dessa aula`}
            inputClassName={errors?.title ? 'invalid' : ' '}
            error={errors?.title}
            label="Título da aula"
          />
          <UnitSelectFields>
            <UnitSelectField>
              <span>Tipo de aula</span>
              <Select
                name="unitType"
                selectType="select2"
                isMulti={false}
                options={unitTypeOptions}
                placeholder="Selecione o tipo da aula"
                onChange={(option: {value: string} | null) => {
                  if (option) _useUseCase.handleChangeField('unitType')({target: {value: option.value}});
                }}
                value={unitTypeOptions.find(option => option.value === unit.unitType)}
              />
            </UnitSelectField>
            <UnitSelectField>
              <span>Tipo de participação</span>
              <Select
                name="participationType"
                selectType="select2"
                isMulti={false}
                options={participationTypeOptions}
                placeholder="Selecione o tipo de participação"
                onChange={(option: {value: string} | null) => {
                  if (option) _useUseCase.handleChangeField('participationType')({target: {value: option.value}});
                }}
                value={participationTypeOptions.find(option => option.value === unit.participationType?.toLowerCase())}
              />
            </UnitSelectField>
          </UnitSelectFields>
          <ButtonRowHeaderContainer>
            <ButtonToggleContentClean
              onPress={_useUseCase.handleToggle}
              buttonState={_useUseCase.toggleContent}
              title="Minimize o conteúdo para editar mais facilmente"
            />
            <ButtonDeleteComponent
              onPress={_useUseCase.onDelete}
              name="Aula"
              title="Apagar a aula"
            />
          </ButtonRowHeaderContainer>
        </UnitContainerHead>
        {_useUseCase.toggleContent === ButtonState.open && (
          <>
            <UnitMediaComponent
              handleMediaUpdate={_useUseCase.handleMediaUpdate}
              coverUrl={unit.coverUrl}
              posterUrl={unit.posterUrl}
              videoUrl={unit.videoUrl}
              audioUrl={unit.audioUrl}
              cover={TranslateMediaValueToFileProps(
                (unit.coverUrl || unit.cover) as unknown as FileProps,
              )}
              poster={TranslateMediaValueToFileProps(
                (unit.posterUrl || unit.poster) as unknown as FileProps,
              )}
              video={TranslateMediaValueToFileProps(
                (unit.videoUrl || unit.video) as unknown as FileProps,
                unit.externalVideoUrl,
              )}
              audio={TranslateMediaValueToFileProps(
                (unit.audioUrl || unit.audio) as unknown as FileProps,
              )}
              alternativeSrc={mediaSourceUrl(unit.alternativeVideo || unit.alternative_video)}
              unit={unit}
            />
            <div className="row">
              <Textarea
                s={12}
                id={`unit-description`}
                onChange={_useUseCase.handleChangeField('description')}
                value={unit.description || ''}
                label={'Descrição*'}
                placeholder={`Escreva aqui a descrição/detalhes de sua aula`}
                className={errors?.description ? 'invalid' : ' '}
                error={errors?.description}
              />
            </div>
          </>
        )}
        {_useUseCase.toggleContent === ButtonState.closed && (
          <UnitContainerMinified>
            <UnitSummaryThumbnail>
              {summary.thumbnail && <img key={summary.thumbnail} src={summary.thumbnail} alt="" onError={event => {event.currentTarget.hidden = true;}} />}
            </UnitSummaryThumbnail>
            <UnitSummaryDetails>
              <strong>{unit.title || 'Aula sem título'}</strong>
              <UnitSummaryBadges>
                {summary.badges.map(badge => <span key={badge}>{badge}</span>)}
                <span>{unit.unitType === 'Free' ? 'Gratuita' : 'Premium'}</span>
              </UnitSummaryBadges>
            </UnitSummaryDetails>
            <UnitDetailsButton type="button" aria-expanded={false} onClick={() => _useUseCase.handleToggle()}>
              Ver detalhes
            </UnitDetailsButton>
          </UnitContainerMinified>
        )}
      </UnitContainer>
    </li>
  );
};

export const UnitEmptyComponent = () => {
  const {createNewUnit, units} = React.useContext(CourseModuleContext);
  const assets = React.useContext(CourseMediaAssetsContext);
  return (
    <UnitContainer className="clean-top add-new">
      <EmptyLesson>
        {!units?.some(unit => !unit._destroy) && <>
          {assets.emptyModule && <img src={assets.emptyModule} alt="" onError={event => {event.currentTarget.hidden = true;}} />}
          <strong>Seu módulo começa com uma aula</strong>
          <p>Organize vídeos, áudios e materiais para seus alunos.</p>
        </>}
        <button type="button" onClick={createNewUnit} aria-label="Adicionar nova aula">Adicionar nova aula</button>
      </EmptyLesson>
    </UnitContainer>
  );
};
